import { Component, computed, inject, input, output } from '@angular/core';
import {
  FilterGlobalStore,
  ObjectSearchGlobalStore,
  type FilterChipEditContext,
  type FilterItem,
  type ObjectSearchRequest,
} from '@bizzdesign/data-access-search';
import type { SystemAttributesUser } from '@bizzdesign/shared/models';
import { InteractiveAppliedFiltersComponent } from '../../components/interactive-applied-filters/interactive-applied-filters.component';

/**
 * Container for the editable applied-filters row.
 *
 * @remarks
 * Owns every filter-state mutation the row can trigger — value edit, removal and
 * clear all — by talking to {@link FilterGlobalStore} directly, and self-serves the
 * relation-object typeahead from {@link ObjectSearchGlobalStore}. Consumers supply
 * only the user typeahead, which lives on the search container store, and react to
 * {@link filtersChanged} by resetting the page index and re-running the search.
 *
 * A value edit that resolves to the same value emits nothing, so committing an
 * unchanged field cannot trigger a redundant search.
 */
@Component({
  selector: 'bd-interactive-applied-filters-container',
  imports: [InteractiveAppliedFiltersComponent],
  templateUrl: './interactive-applied-filters-container.component.html',
})
export class InteractiveAppliedFiltersContainerComponent {
  users = input<SystemAttributesUser[]>([]);
  usersLoading = input<boolean>(false);

  searchUsers = output<string>();
  filtersChanged = output<void>();

  private readonly filterGlobalStore = inject(FilterGlobalStore);
  private readonly objectSearchGlobalStore = inject(ObjectSearchGlobalStore);

  readonly appliedFilters = this.filterGlobalStore.appliedFilters;
  readonly attributeCategoryDefinitions =
    this.filterGlobalStore.attributeCategoryDefinitions;

  readonly editContext = computed<FilterChipEditContext>(() => ({
    objectSuggestions: this.objectSearchGlobalStore.searchSuggestions(),
    objectSuggestionsLoading:
      this.objectSearchGlobalStore.searchSuggestionsLoading(),
    users: this.users(),
    usersLoading: this.usersLoading(),
    authenticatedUser: this.filterGlobalStore.authenticatedUser(),
  }));

  onUpdateFilter(appliedFilterItem: FilterItem): void {
    const hasChanged =
      this.filterGlobalStore.updateAppliedFilterInFilterState(
        appliedFilterItem,
      );
    if (hasChanged) {
      this.filtersChanged.emit();
    }
  }

  onRemoveFilter(appliedFilterItem: FilterItem): void {
    this.filterGlobalStore.removeAppliedFilterFromFilterState(
      appliedFilterItem,
    );
    this.filtersChanged.emit();
  }

  onClearAllFilters(): void {
    this.filterGlobalStore.clearAllFilters();
    this.filtersChanged.emit();
  }

  onSearchObjects({ searchTerm, referenceType }: ObjectSearchRequest): void {
    this.objectSearchGlobalStore.updateReferenceType(referenceType);
    // An empty term matches nothing, so drop the results rather than search for them.
    if (searchTerm === '') {
      this.objectSearchGlobalStore.updateSearchSuggestions([]);
      return;
    }
    this.objectSearchGlobalStore.searchObjectSuggestions(searchTerm);
  }
}
